// src/screens/LoginScreen.tsx
import React, { useState, useRef, useEffect } from 'react';
import './parts/LoginScreen.styles.css';
import './parts/LoginScreen.auth-flows.css';
import skoolpillarLogo from './parts/skoolpillar_logo.png';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useAuth } from '../../hooks/useAuth';
import {
  requestLoginOtp,
  verifyLoginOtp,
  requestPasswordResetOtp,
  resetPasswordWithOtp,
} from '../../stateManagers/services/authService';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

// password = normal login | otp = login with emailed code | forgot = reset password with emailed code
type AuthMode = 'password' | 'otp' | 'forgot';
type OtpStep = 'request' | 'verify';

const OTP_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 6;

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // OTP / forgot-password flow state
  const [mode, setMode] = useState<AuthMode>('password');
  const [step, setStep] = useState<OtpStep>('request');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const [leaving, setLeaving] = useState(false); // true while the current pane fades out
  const leaveTimer = useRef<number | undefined>(undefined);

  const { login } = useAuth();
  const toast = useRef<Toast>(null);

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const warn = (detail: string) =>
    toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail, life: 3000 });

  const showError = (summary: string, err: any, fallback: string) => {
    const errorMsg = err?.message || fallback;
    setLoginError(errorMsg);
    toast.current?.show({ severity: 'error', summary, detail: errorMsg, life: 4000 });
  };

  // Fade the current pane out, apply the state change, then the new pane fades in
  // (the form remounts via its `key`, which replays the enter animation).
  const animateTo = (apply: () => void) => {
    if (leaving) {
      apply();
      return;
    }
    setLeaving(true);
    leaveTimer.current = window.setTimeout(() => {
      apply();
      setLeaving(false);
    }, 180);
  };

  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  const switchMode = (next: AuthMode) =>
    animateTo(() => {
      setMode(next);
      setStep('request');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setLoginError('');
      setResendIn(0);
    });

  const validateForm = () => {
    let isValid = true;

    if (!email.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please enter your username or email', life: 3000 });
      isValid = false;
    }

    if (!password.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please enter your password', life: 3000 });
      isValid = false;
    }

    return isValid;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      const errorMsg = err.message || 'Login failed. Please check your credentials.';
      setLoginError(errorMsg);
      toast.current?.show({ severity: 'error', summary: 'Login Failed', detail: errorMsg, life: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ── OTP: send / resend code (login + forgot) ──────────────────────────────
  const sendCode = async () => {
    setLoginError('');
    if (!email.trim()) {
      warn('Please enter your username or email');
      return;
    }

    setSubmitting(true);
    try {
      const res =
        mode === 'forgot'
          ? await requestPasswordResetOtp(email.trim())
          : await requestLoginOtp(email.trim());

      const showVerify = () => {
        setStep('verify');
        setOtp('');
        setResendIn(res.resend_after_seconds ?? 60);
        setExpiryMinutes(Math.max(1, Math.round((res.expires_in_seconds ?? 600) / 60)));
      };
      // Animate only the first send; a "Resend" while already on verify just updates in place.
      if (step === 'request') animateTo(showVerify);
      else showVerify();
      toast.current?.show({
        severity: 'info',
        summary: 'Check your email',
        detail: 'If an account exists, a code has been sent to its registered email.',
        life: 4000,
      });
    } catch (err: any) {
      showError('Could not send code', err, 'Failed to send OTP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── OTP login: verify code ────────────────────────────────────────────────
  const handleOtpLogin = async () => {
    setLoginError('');
    if (otp.length !== OTP_LENGTH) {
      warn(`Please enter the ${OTP_LENGTH}-digit code`);
      return;
    }

    setSubmitting(true);
    try {
      await verifyLoginOtp(email.trim(), otp);
      // Tokens are now in cookies. Reload so the app boots authenticated.
      window.location.assign('/');
    } catch (err: any) {
      showError('Login Failed', err, 'Invalid or expired OTP.');
      setSubmitting(false);
    }
  };

  // ── Forgot password: verify code + set new password ──────────────────────
  const handleResetPassword = async () => {
    setLoginError('');
    if (otp.length !== OTP_LENGTH) {
      warn(`Please enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      warn(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      warn('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await resetPasswordWithOtp(email.trim(), otp, newPassword);
      switchMode('password');
      setPassword('');
      toast.current?.show({
        severity: 'success',
        summary: 'Password reset',
        detail: 'Your password has been updated. Please sign in.',
        life: 4000,
      });
    } catch (err: any) {
      showError('Reset Failed', err, 'Invalid or expired OTP.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    if (mode === 'password') {
      handleLoginSubmit(e);
      return;
    }
    e.preventDefault();
    if (step === 'request') {
      sendCode();
    } else if (mode === 'otp') {
      handleOtpLogin();
    } else {
      handleResetPassword();
    }
  };

  const subtitle =
    mode === 'password'
      ? 'Sign in to your account'
      : mode === 'otp'
        ? 'Sign in with a one-time code'
        : 'Reset your password';

  const showIdentifier = mode === 'password' || step === 'request';
  const inVerifyStep = mode !== 'password' && step === 'verify';

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div className="login-container">
        <div className="login-card">
          {/* Left Panel */}
          <div className="login-left">
            {/* glowing light columns rising from the bottom-left */}
            <div className="light-grain" aria-hidden="true" />
            <div className="light-glow" aria-hidden="true" />
            <div className="light-columns" aria-hidden="true">
              <span className="light-col" />
              <span className="light-col" />
              <span className="light-col" />
              <span className="light-col" />
              <span className="light-col" />
            </div>

            <div className="login-left-content">
              <div className="brand-row">
                <img className="brand-logo" src={skoolpillarLogo} alt="SkoolPillar" />
              </div>

              <div className="hero-copy">
                <h1 className="main-heading">
                  Your whole school, in one calm place.
                </h1>
                <p className="tagline">
                  Attendance, marks, homework and notices, organised for teachers, staff and parents.
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="login-right">
            <div className="form-head">
              <h2 className="form-title">
                <span className="form-title-lead">Welcome to</span>
                <span className="form-title-brand" data-text="SkoolPillar">SkoolPillar</span>
              </h2>
              <p key={subtitle} className="form-subtitle auth-fade">{subtitle}</p>
            </div>

            {loginError && (
              <Message severity="error" className='custominline' text={loginError} style={{ marginBottom: '1.5rem', width: '100%' }} />
            )}

            <div className="form-content">
              <form
                key={`${mode}-${step}`}
                className={`auth-pane${leaving ? ' leaving' : ''}`}
                onSubmit={handleFormSubmit}
              >
                {/* Identifier: password mode, and the "send code" step of OTP / forgot */}
                {showIdentifier && (
                  <div className="input-wrapper">
                    <GlobalInputField
                      label="Username or Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your username or email"
                      type="text"
                      required
                      icon="pi pi-user"
                      autoComplete="username"
                    />
                  </div>
                )}

                {/* ───────────── Password login ───────────── */}
                {mode === 'password' && (
                  <>
                    <div className="input-wrapper">
                      <GlobalInputField
                        label="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        type="password"
                        required
                        icon="pi pi-lock"
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="remember-forgot">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Checkbox inputId="remember" checked={rememberMe} onChange={(e) => setRememberMe(e.checked ?? false)} />
                        <label htmlFor="remember" style={{ color: '#4b5563', cursor: 'pointer' }}>Remember me</label>
                      </div>
                      <a
                        href="#"
                        className="forgot-link"
                        onClick={(e) => {
                          e.preventDefault();
                          switchMode('forgot');
                        }}
                      >
                        Forgot Password?
                      </a>
                    </div>

                    <GlobalButton
                      label={submitting ? 'Signing in...' : 'Sign In'}
                      type="submit"
                      variant="primary"
                      size="large"
                      fullWidth
                      loading={submitting}
                      disabled={submitting}
                      icon="pi pi-sign-in"
                      iconPos="left"
                    />

                    <div className="auth-divider"><span>or</span></div>
                    <div className="auth-alt-row">
                      <button
                        type="button"
                        className="auth-link-btn"
                        onClick={() => switchMode('otp')}
                      >
                        <i className="pi pi-envelope" /> Sign in with an email code
                      </button>
                    </div>
                  </>
                )}

                {/* ───────────── OTP / forgot: send code ───────────── */}
                {mode !== 'password' && step === 'request' && (
                  <>
                    <p className="otp-hint">
                      {mode === 'otp'
                        ? "We'll email a one-time code to the address registered on your account."
                        : "Enter your username or email and we'll email you a code to reset your password."}
                    </p>

                    <GlobalButton
                      label={submitting ? 'Sending...' : 'Send Code'}
                      type="submit"
                      variant="primary"
                      size="large"
                      fullWidth
                      loading={submitting}
                      disabled={submitting}
                      icon="pi pi-envelope"
                      iconPos="left"
                    />
                  </>
                )}

                {/* ───────────── OTP / forgot: verify code ───────────── */}
                {inVerifyStep && (
                  <>
                    <div className="otp-info">
                      A {OTP_LENGTH}-digit code was sent for <strong>{email.trim()}</strong>. It expires in {expiryMinutes} minutes.{' '}
                      <button
                        type="button"
                        className="auth-link-btn"
                        onClick={() =>
                          animateTo(() => {
                            setStep('request');
                            setOtp('');
                            setLoginError('');
                          })
                        }
                      >
                        Change
                      </button>
                    </div>

                    <div className="input-wrapper otp-code-field">
                      <GlobalInputField
                        label={`${OTP_LENGTH}-digit Code`}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                        placeholder="••••••"
                        type="text"
                        required
                        icon="pi pi-shield"
                        autoComplete="one-time-code"
                      />
                    </div>

                    {mode === 'forgot' && (
                      <>
                        <div className="input-wrapper">
                          <GlobalInputField
                            label="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="At least 6 characters"
                            type="password"
                            required
                            icon="pi pi-lock"
                            autoComplete="new-password"
                          />
                        </div>
                        <div className="input-wrapper">
                          <GlobalInputField
                            label="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter new password"
                            type="password"
                            required
                            icon="pi pi-lock"
                            autoComplete="new-password"
                          />
                        </div>
                      </>
                    )}

                    <div className="otp-resend-row">
                      <span>Didn't get the code?</span>
                      <button
                        type="button"
                        className="auth-link-btn"
                        onClick={sendCode}
                        disabled={resendIn > 0 || submitting}
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                      </button>
                    </div>

                    <GlobalButton
                      label={
                        mode === 'otp'
                          ? (submitting ? 'Verifying...' : 'Verify & Sign In')
                          : (submitting ? 'Resetting...' : 'Reset Password')
                      }
                      type="submit"
                      variant="primary"
                      size="large"
                      fullWidth
                      loading={submitting}
                      disabled={submitting}
                      icon={mode === 'otp' ? 'pi pi-sign-in' : 'pi pi-key'}
                      iconPos="left"
                    />
                  </>
                )}

                {/* Back to password sign-in (OTP / forgot modes) */}
                {mode !== 'password' && (
                  <div className="auth-alt-row">
                    <button
                      type="button"
                      className="auth-link-btn"
                      onClick={() => switchMode('password')}
                    >
                      <i className="pi pi-arrow-left" /> Back to sign in
                    </button>
                  </div>
                )}

                <p className="signup-text">
                  Don't have an account? <a href="https://www.skoolpillar.com/contact-us/" target="_blank" rel="noopener noreferrer" className="signup-link">Contact Administrator</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;